import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  TablePagination,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { get_company_list, setCompanyPage, } from "../../../store/company/companySlice";
import { COMPANY_LIMIT } from "../../../helpers/constants";
import TablePaginationActions from "../TablePaginationActions";

const columns = [
  { id: "name", label: "Company Name" },
  { id: "company_logo", label: "Company Logo" },
  { id: "payment_type", label: "Payment Type" },
  { id: "company_domain", label: "Domain" },
  { id: "is_active", label: "Status" },
  { id: "action", label: "Actions" },
];

function CompanyTable(props) {
  const { is_loading,
    total_company_count,
    is_company_modal_open,
    handleCompanyUpdateModal,
    activate_deactivate_company,
  } = props;
  const { company_list, page } = useSelector((state) => state.company);
  const dispatch = useDispatch();
  const rowsPerPage = COMPANY_LIMIT

  const handleStatusChange = async (row) => {
    let newStatus = row.is_active ? 'dc' : 'ac';
    await activate_deactivate_company({ company_id: row.id, updated_data: newStatus });
  }

  const handleModal = async (c_action, company_id) => {
    await handleCompanyUpdateModal(!is_company_modal_open, c_action, company_id);
  };

  useEffect(() => {
    dispatch(get_company_list({ page: page + 1, limit: COMPANY_LIMIT }));
  }, [page, dispatch]);

  const handlePageChange = (event, newPage) => {
    dispatch(setCompanyPage(newPage));
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer
          sx={{
            maxHeight: { xl: 800, md: 440 },
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "4px",
            },
          }}>
          <Table stickyHeader aria-label="sticky table"
            sx={{
              "& .MuiTableCell-stickyHeader": {
                backgroundColor: "#f5f5f5",
                fontWeight: "bold",
                zIndex: 2,
              },
            }}>
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-root:not(:first-of-type)": {
                    textAlign: "left",

                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#eeeeee70",
                    }}
                  >{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {company_list.length > 0 ? (
                company_list.map((row, idx) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                    <TableCell align="left">
                      <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }}>
                        <Tooltip title={row.name || "-"} arrow>
                          <span>{row.name || "-"}</span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="left">
                      {row.company_logo ? (
                        <Tooltip title={row.company_logo.split('/').pop()} arrow>
                          <img
                            src={row.company_logo}
                            alt="Company Logo"
                            style={{ maxWidth: "100px", maxHeight: "100px", cursor: 'pointer' }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="No Logo Uploaded" arrow>
                          <span>No Logo</span>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell align="left">
                      <Tooltip title={row.payment_terms?.payment_type || "-"} arrow>
                        <span>{row.payment_terms?.payment_type || "-"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">
                      <Tooltip title={row.company_domain || "-"} arrow>
                        <span>{row.company_domain || "-"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">
                      <Tooltip title={row.is_active ? "Active" : "Inactive"} arrow>
                        <span>{row.is_active ? "Active" : "Inactive"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" justifyContent="left" spacing={2}>
                        {/* Edit button */}
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            aria-label="edit"
                            color="primary"
                            onClick={() => handleModal("UPDATE", row.id)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              style={{ width: "1.5rem" }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 
                                16.07a4.5 4.5 0 0 1-1.897 1.13L6 
                                18l.8-2.685a4.5 4.5 0 0 
                                1 1.13-1.897l8.932-8.931Zm0 
                                0L19.5 7.125M18 14v4.75A2.25 
                                2.25 0 0 1 15.75 21H5.25A2.25 
                                2.25 0 0 1 3 18.75V8.25A2.25 
                                2.25 0 0 1 5.25 6H10"
                              />
                            </svg>
                          </IconButton>
                        </Tooltip>

                        {/* Activate/Deactivate */}
                        <Tooltip title={row.is_active ? 'Active' : 'Inactive'} arrow>
                          <IconButton
                            type="button"
                            aria-label="toggle-status"
                            color="primary"
                            onClick={() => handleStatusChange(row)}
                          >
                            {row.is_active ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : !is_loading && company_list.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    No Record Found
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    loading....
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {/* Pagination */}
            {company_list.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <TablePagination
                      component="div"
                      count={total_company_count}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[]}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

export default CompanyTable;
